import React from "react";
import { useParams, Navigate } from "react-router-dom";
import Header from "@/components/Header";

/* InfoPage — one component, three static content slugs.
 *  - /faq, /shipping, /returns
 * Renders inside the brand frame with consistent typography. */

const CONTENT = {
  faq: {
    title: "Frequently Asked Questions",
    intro: "Everything you wanted to know about Substitute Teacher merch, in one place.",
    sections: [
      {
        q: "When does Substitute Teacher premiere?",
        a: "The show is rolling out exclusively on Tubi TV. Drop 01 of the official merch is live now; new episodes and capsules drop weekly. Sign up for the newsletter to be the first to know.",
      },
      {
        q: "Who designs and ships the merch?",
        a: "Every hoodie, sweatshirt, tee and cropped tee is designed, printed and fulfilled by Revurge — Wall of Entertainment's print-on-demand, tech and logistics partner. Each piece is made-to-order and quality-checked before it leaves the warehouse.",
      },
      {
        q: "How long does production take?",
        a: "Made-to-order garments take 2–4 business days to print, then 3–7 business days to ship within the U.S. International orders take 7–14 business days depending on the destination.",
      },
      {
        q: "Can I cancel or modify an order?",
        a: "Because every item is print-on-demand, we can only cancel or modify within 6 hours of payment. After that the garment has already entered production. Email orders@wallofentertainment.com from the same address you used at checkout.",
      },
      {
        q: "Where can I track my order?",
        a: "After your shipment leaves the warehouse you'll get a tracking link via email and on your order success page. You can also enter your order number + email at /track to pull it up any time.",
      },
      {
        q: "Do you ship internationally?",
        a: "Yes. We ship globally via Easyship couriers (UPS, DHL, USPS, FedEx and local partners). Final shipping cost is calculated at checkout and any import duties are the buyer's responsibility.",
      },
      {
        q: "Is this an official Charles Brockman III / Wall of Entertainment store?",
        a: "Yes — this is the only official storefront for Substitute Teacher and The Know It Owls. Anything sold elsewhere isn't authorized by Wall of Entertainment.",
      },
    ],
  },
  shipping: {
    title: "Shipping Policy",
    intro: "Made-to-order means a couple of extra days at the press — but every piece is printed for you, not pulled off a shelf.",
    sections: [
      { q: "Processing time", a: "2–4 business days to print and quality-check before pickup." },
      { q: "Domestic (United States)", a: "3–7 business days via USPS, UPS or FedEx (auto-selected by Easyship for the best rate)." },
      { q: "International", a: "7–14 business days via DHL Express, UPS Worldwide or local equivalents. Duties / taxes are the buyer's responsibility per the destination country's rules." },
      { q: "Free shipping", a: "Orders over $100 ship free within the U.S." },
      { q: "Tracking", a: "Every shipment generates an Easyship tracking link emailed to you and visible on the order success page. You can also look it up any time at /track." },
      { q: "Lost or stuck packages", a: "If a tracker hasn't moved for 5+ business days, email orders@wallofentertainment.com — we'll work with the courier to locate it or re-ship." },
    ],
  },
  returns: {
    title: "Returns & Exchanges",
    intro: "Because every garment is made-to-order, we handle returns case-by-case to keep waste low and quality high.",
    sections: [
      { q: "30-day window", a: "Defects, misprints or damage in transit are eligible for a full refund or free reprint when reported within 30 days of delivery." },
      { q: "Size exchanges", a: "We don't currently stock inventory for swap-in-swap-out exchanges. If you need a different size, email us within 30 days and we'll quote a discounted reprint at our cost (no markup)." },
      { q: "How to start a return", a: "Email returns@wallofentertainment.com with your order number, a description of the issue and a photo. We respond within 1 business day." },
      { q: "Refunds", a: "Approved refunds are issued to the original Stripe payment method within 5–10 business days of the return being received (defects/misprints don't require physical return)." },
      { q: "Final-sale items", a: "Marked-down drops, holiday capsules and personalized pieces are final sale unless defective." },
    ],
  },
};

export default function Info({ slug: slugProp }) {
  const params = useParams();
  const slug = slugProp || params.slug;
  const data = CONTENT[slug];
  if (!data) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-[var(--st-bg)]" data-testid={`info-page-${slug}`}>
      <Header />

      <main className="max-w-[1100px] mx-auto px-4 md:px-6 py-10">
        <h1 className="font-display uppercase tracking-tight text-5xl md:text-6xl text-[var(--st-maroon)] leading-none">
          {data.title}
        </h1>
        <p className="mt-3 font-body text-base md:text-lg text-[var(--st-fg)]/80 max-w-2xl">
          {data.intro}
        </p>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          {data.sections.map((s, i) => (
            <section
              key={i}
              data-testid={`info-section-${i}`}
              className="border-2 border-[#111] bg-[var(--st-cream)] shadow-brutal-sm p-4 md:p-5"
            >
              <h2 className="font-display uppercase tracking-tight text-xl text-[var(--st-fg)]">{s.q}</h2>
              <p className="mt-2 font-body text-sm text-[var(--st-fg)]/80 leading-relaxed">{s.a}</p>
            </section>
          ))}
        </div>
      </main>
    </div>
  );
}
